
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Globe } from "lucide-react";
import AdminPageBackground from "@/components/admin/AdminPageBackground";
import { Button } from "@/components/ui/button";
import AdminEventsList from "@/components/admin/AdminEventsList";

interface ScrapedEvent {
  id: string;
  source_name: string;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  location_ar: string | null;
  image_url: string | null;
  category: string | null;
  external_link: string | null;
  is_published: boolean;
  scraped_at: string;
  ticket_status: string | null;
  last_status_check: string | null;
}

const Exhibitions = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [events, setEvents] = useState<ScrapedEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("scraped_events")
      .select("*")
      .eq("category", "exhibitions")
      .order("date", { ascending: true });
    
    if (data && !error) {
      setEvents(data);
    }
    setIsLoading(false);
  };

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/admin-dashboard"
              className="flex items-center gap-2 text-primary-foreground hover:text-white/80 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="rounded-full text-white/70 hover:text-white hover:bg-white/10"
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">
            {isRTL ? "إدارة المعارض" : "Exhibition Management"}
          </h1>
          <span className="text-white/50">
            {isRTL ? `${events.length} معرض` : `${events.length} exhibitions`}
          </span>
        </div>

        <AdminEventsList 
          events={events}
          setEvents={setEvents}
          isLoading={isLoading}
        />
      </main>
    </AdminPageBackground>
  );
};

export default Exhibitions;
